/**
 * What the customer platform offers, and what an organization can switch off.
 *
 * This is a description of `seiki_web`, not of this console — hence its own
 * directory, away from `src/pages/`, which holds the backoffice's own screens.
 * It exists so an operator can answer "what will this org's people actually
 * see?" without a seiki_web checkout and a login as one of them.
 *
 * Two rules decide every row, and they are answered in different places:
 *
 *   * **Module gating** — per customer, and the only half this console can
 *     change. Three areas can be switched off (`GATEABLE_MODULES`); the
 *     block-list lives on `organizations.disabled_modules`, falling back to the
 *     tenant's `features.disabled_modules`. See
 *     `seiki-backend/app/core/platform_modules.py`.
 *   * **Role gating** — fixed in the platform's code, the same for every
 *     customer. Mirrored here from `seiki_web/src/layouts/MainLayout/index.jsx`
 *     (`allMenuItems`) and `seiki_web/src/pages/account/rights.js`. Nothing in
 *     the backoffice can move it, which is exactly why it is worth showing: it
 *     is the half people are surprised by.
 *
 * Being a hand-kept mirror, it drifts if MainLayout changes and nobody looks
 * here. It is a *display* of the platform's rule, never an enforcement of it —
 * the platform gates itself; a wrong row here misinforms an operator rather
 * than opening a door.
 */

import type { EffectiveRole } from '../types';

/** Areas that can be switched off per customer.
 *
 * Order is canonical and matches `GATEABLE_MODULES` in the backend and in
 * seiki_web's TenantProvider. Keys are hyphenated because they are the
 * platform's sidebar/route keys, not API field names.
 */
export type GateableModule = 'campaigns' | 'media-plans' | 'billboard-types';

export const GATEABLE_MODULES: GateableModule[] = [
  'campaigns',
  'media-plans',
  'billboard-types',
];

export const MODULE_LABEL: Record<string, string> = {
  campaigns: 'Campaigns',
  'media-plans': 'Media Plans',
  'billboard-types': 'Billboard Types',
};

export function moduleLabel(value: string): string {
  return MODULE_LABEL[value] ?? value;
}

/** The three roles the platform's menu gates on — `EffectiveRole` server-side.
 *
 * Nothing stores this per user any more (AD-15): it is computed from the org
 * position and the workspace membership. It is still the axis the sidebar
 * reads, so the matrix is drawn against it.
 */
export const PLATFORM_ROLES: EffectiveRole[] = ['admin', 'team_member', 'external'];

export const PLATFORM_ROLE_LABEL: Record<EffectiveRole, string> = {
  admin: 'Admin',
  team_member: 'Team member',
  external: 'External',
};

export interface PlatformPage {
  /** The platform's menu key. */
  key: string;
  label: string;
  /** Set for the entries nested under Settings in the sidebar. */
  section?: string;
  /** Route in seiki_web, for operators who want to check by hand. */
  path: string;
  /** Module that can hide it; absent means no customer can switch it off. */
  module?: GateableModule;
  /** Roles that may see it; `null` means every role does. */
  roles: EffectiveRole[] | null;
  /** Why the row reads the way it does, shown as a tooltip. */
  note?: string;
}

/**
 * Every sidebar entry, in the order the platform renders them.
 *
 * Public Inventory is left out: its menu item is commented out in MainLayout,
 * so no one sees it today and listing it would promise a page that isn't there.
 */
export const PLATFORM_PAGES: PlatformPage[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    path: '/app/dashboard',
    roles: null,
    note: 'Its Campaigns and Media Plans sections follow those two modules, so switching one off empties part of this page rather than hiding it.',
  },
  {
    key: 'my-inventory',
    label: 'My Inventory',
    path: '/app/my-inventory',
    roles: null,
    note: 'Viewing is open to everyone. Uploading and deleting are capability grants, per workspace — see the Accesses screen.',
  },
  {
    key: 'scenarios',
    label: 'Scenario Planning',
    path: '/app/scenarios',
    roles: ['admin', 'team_member'],
    note: 'Hidden from external users by the platform, for every customer.',
  },
  {
    key: 'campaigns',
    label: 'Campaigns',
    path: '/app/campaigns',
    module: 'campaigns',
    roles: null,
  },
  {
    key: 'media-plans',
    label: 'Media Plans',
    path: '/app/media-plans',
    module: 'media-plans',
    roles: null,
  },
  {
    key: 'settings-users',
    label: 'Users',
    section: 'Settings',
    path: '/app/settings/users',
    roles: ['admin', 'external'],
    note: 'External users keep this entry, but the org-level gates read users.org_role — an external who is not an org admin can open it and do little.',
  },
  {
    key: 'settings-billboard-types',
    label: 'Billboard Types',
    section: 'Settings',
    path: '/app/settings/billboard-types',
    module: 'billboard-types',
    roles: ['admin'],
  },
  {
    key: 'settings-networks',
    label: 'Networks',
    section: 'Settings',
    path: '/app/settings/networks',
    roles: null,
    note: 'Visible to everyone; read-only unless the user holds the edit-networks capability.',
  },
  {
    key: 'settings-faq',
    label: 'FAQ',
    section: 'Settings',
    path: '/app/settings/faq',
    roles: null,
  },
];

/** Why a page is invisible — the two answers have different owners. */
export type PageVisibility = 'visible' | 'module-off' | 'role';

export function pageVisibility(
  page: PlatformPage,
  role: EffectiveRole,
  disabledModules: string[],
): PageVisibility {
  // The module is asked first because it hides the page from everyone: saying
  // "off for this customer" is more useful than "and also not your role".
  if (page.module && disabledModules.includes(page.module)) return 'module-off';
  if (page.roles && !page.roles.includes(role)) return 'role';
  return 'visible';
}

/** The pages one role actually sees, given the customer's block-list. */
export function visiblePages(
  role: EffectiveRole,
  disabledModules: string[],
): PlatformPage[] {
  return PLATFORM_PAGES.filter(
    (page) => pageVisibility(page, role, disabledModules) === 'visible',
  );
}

/** Modules left on, in canonical order. Complement of the block-list. */
export function enabledModules(disabledModules: string[]): GateableModule[] {
  return GATEABLE_MODULES.filter((m) => !disabledModules.includes(m));
}

/* ------------------------------------------------------------------------- *
 * The other five settings.
 *
 * Page access is a block-list over the three modules above. These are the rest
 * of what an organization holds, and they come in two more shapes — see
 * `OrgFeatures` in src/types.ts for why the difference is load-bearing.
 * ------------------------------------------------------------------------- */

/** KPIs the platform can compute and show. Allow-list. */
export const ALL_METRIC_KEYS = [
  'net_contacts',
  'gross_contacts',
  // Venue markets only (France PowerFull). platform-api omits the field for
  // every other market, so an org that leaves this list unrestricted only ever
  // sees the metric where it exists — but a venue tenant whose list IS
  // restricted needs the box here, or it cannot be turned on at all.
  'visits',
  'frequency',
  'coverage_percent',
  'grp',
  'population',
  'cpm',
  'attention_index',
] as const;

/** Breakdown charts the platform can render. Allow-list. */
export const ALL_CHART_KEYS = [
  'hour',
  'week',
  'daily_contacts',
  'gender',
  'age',
  'social_grade',
  'provenance',
  'mode',
  'locations',
] as const;

/** Campaign types an org may create. Allow-list. */
export const ALL_CAMPAIGN_TYPES = [
  'standard_campaign',
  'tower_campaign',
  'mix_campaign',
] as const;

/** Billboard fields that can be hidden from the inventory panels. Block-list. */
export const GATEABLE_INVENTORY_FIELDS = [
  'is_tower',
  'is_indoor',
  'width',
  'height',
  'azimuth_from_north',
  'height_from_ground',
] as const;

export const METRIC_LABEL: Record<string, string> = {
  net_contacts: 'Net contacts',
  gross_contacts: 'Gross contacts',
  visits: 'Visits',
  frequency: 'Frequency',
  coverage_percent: 'Reach',
  grp: 'GRP',
  population: 'Population',
  cpm: 'CPM',
  attention_index: 'Attention index',
};

export const CHART_LABEL: Record<string, string> = {
  hour: 'Hourly distribution',
  week: 'Day of week',
  daily_contacts: 'Daily contacts timeline',
  gender: 'Gender',
  age: 'Age',
  social_grade: 'Social grade',
  provenance: 'Geographic breakdown',
  mode: 'Transport mode',
  locations: 'Billboard locations map',
};

export const CAMPAIGN_TYPE_LABEL: Record<string, string> = {
  standard_campaign: 'Standard',
  tower_campaign: 'Tower',
  mix_campaign: 'Mix',
};

export const INVENTORY_FIELD_LABEL: Record<string, string> = {
  is_tower: 'Tower',
  is_indoor: 'Indoor',
  width: 'Width',
  height: 'Height',
  azimuth_from_north: 'Azimuth from north',
  height_from_ground: 'Height from ground',
};

/** One editable setting, described so the console can render it generically. */
export interface FeatureSpec {
  /** The API field name, and the column name. */
  key:
    | 'disabled_modules'
    | 'hidden_inventory_fields'
    | 'campaign_types'
    | 'visible_metrics'
    | 'visible_charts';
  label: string;
  /** What ticking a box means, which differs between the two shapes. */
  kind: 'block' | 'allow';
  keys: readonly string[];
  labels: Record<string, string>;
  /** Shown under the control. */
  help: string;
}

/**
 * Every list setting, in the order the drawer shows them.
 *
 * `kind` is the thing to read carefully. A **block-list** stores what is hidden,
 * so the console shows it inverted — ticked means visible — and an empty stored
 * list hides nothing. An **allow-list** stores what is permitted, and `null`
 * means *unrestricted*: not the same as ticking every box today, because only
 * `null` keeps following the platform when a new metric ships.
 */
export const FEATURE_SPECS: FeatureSpec[] = [
  {
    key: 'disabled_modules',
    label: 'Pages',
    kind: 'block',
    keys: GATEABLE_MODULES,
    labels: MODULE_LABEL,
    help: 'Whole areas of the platform. Unticking one hides its sidebar entry and its routes from everyone in this organization.',
  },
  {
    key: 'campaign_types',
    label: 'Campaign types',
    kind: 'allow',
    keys: ALL_CAMPAIGN_TYPES,
    labels: CAMPAIGN_TYPE_LABEL,
    help: 'Which kinds of campaign they may create.',
  },
  {
    key: 'visible_metrics',
    label: 'Metrics',
    kind: 'allow',
    keys: ALL_METRIC_KEYS,
    labels: METRIC_LABEL,
    help: 'KPIs shown across the app, the PDF report and CSV/Excel exports. The backend applies this list too, so unticking one removes it from exports as well as from the screen.',
  },
  {
    key: 'visible_charts',
    label: 'Charts',
    kind: 'allow',
    keys: ALL_CHART_KEYS,
    labels: CHART_LABEL,
    help: 'Breakdown charts shown across the app, the PDF report and exports.',
  },
  {
    key: 'hidden_inventory_fields',
    label: 'Inventory fields',
    kind: 'block',
    keys: GATEABLE_INVENTORY_FIELDS,
    labels: INVENTORY_FIELD_LABEL,
    help: 'Billboard attributes shown in the inventory panels.',
  },
];
